/* ============================================================
   CART VALIDATION ENGINE — Phase 1
   Handles: master Control #, per-line VIN, per-line plate text,
   group-based VIN sync, checkout button gating.
   ============================================================ */

(function () {
  'use strict';

  // ---- Configuration ----
  const CONTROL_NUMBER_REGEX = /^[LCDFR][A-Za-z0-9]{7}$/;
  const VIN_REGEX = /^[A-Z0-9-]{2,20}$/;
  const PLATE_REGEX_TEMPLATE = (max) => new RegExp(`^[A-Z0-9 &]{2,${max}}$`);

  // ---- State ----
  const state = {
    masterControlNumber: '',
    lines: {}, // keyed by lineIndex: { fields: {fieldName: {value, valid}} }
    initialized: false,
  };

  // ---- DOM helpers ----
  function $(selector, root = document) { return root.querySelector(selector); }
  function $$(selector, root = document) { return Array.from(root.querySelectorAll(selector)); }

  // ---- Input sanitization (matches existing product page JS) ----
  function sanitizePlateInput(value, maxChars) {
    return value.toUpperCase().replace(/O/g, '0').substring(0, maxChars);
  }
  function sanitizeVinInput(value) {
    return value.toUpperCase().replace(/[^A-Z0-9-]/g, '').substring(0, 20);
  }
  function sanitizeControlNumberInput(value) {
    return value.toUpperCase().substring(0, 8);
  }

  // ---- Validation ----
  function validateControlNumber(value) {
    return CONTROL_NUMBER_REGEX.test(value);
  }
  function validateVin(value) {
    return VIN_REGEX.test(value);
  }
  function validatePlate(value, maxChars) {
    return PLATE_REGEX_TEMPLATE(maxChars).test(value);
  }

  // ---- Shopify cart API ----
  // Updates a single line's properties via /cart/change.js
  // line is 1-indexed
  async function updateLineProperties(lineIndex, properties) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line: lineIndex,
          properties: properties,
        }),
      });
      if (!response.ok) {
        console.error('[cart-validation] /cart/change.js failed', response.status);
        return null;
      }
      return await response.json();
    } catch (err) {
      console.error('[cart-validation] update error', err);
      return null;
    }
  }

  // Debounce helper so we don't hammer the API on every keystroke
  function debounce(fn, ms) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), ms);
    };
  }

  // ---- Field handlers ----

  function handleMasterControlNumberInput(event) {
    const input = event.target;
    const cleaned = sanitizeControlNumberInput(input.value);
    if (input.value !== cleaned) input.value = cleaned;

    const isValid = validateControlNumber(cleaned);
    state.masterControlNumber = cleaned;

    input.classList.toggle('is-valid', isValid && cleaned.length > 0);
    input.classList.toggle('is-invalid', !isValid && cleaned.length > 0);

    const errorEl = $('#cart-master-control-error');
    if (errorEl) errorEl.style.display = (!isValid && cleaned.length > 0) ? 'block' : 'none';

    // Propagate to all Additional vehicle lines as their Control Number property
    if (isValid) {
      propagateMasterControlNumber(cleaned);
    }

    runOverallValidation();
  }

const debouncedPropagateControlNumber = debounce(async function (value) {
    const llcLevelLines = $$('.cart-line-validation[data-line-type="additional"], .cart-line-validation[data-line-type="transfer-ownership"], .cart-line-validation[data-line-type="transfer-agency"], .cart-line-validation[data-line-type="duplicate-title"], .cart-line-validation[data-line-type="llc-renewal"], .cart-line-validation[data-line-type="llc-restoration"], .cart-line-validation[data-line-type="tag-temp"], .cart-line-validation[data-line-type="tag-custom-vehicle"], .cart-line-validation[data-line-type="tag-custom-mc-sxs"], .cart-line-validation[data-line-type="tag-custom-background"]');
    for (const lineEl of llcLevelLines) {
      const lineIndex = parseInt(lineEl.dataset.lineIndex, 10);
      const existingProps = collectLineProperties(lineEl);
      existingProps['Control Number'] = value;
      await updateLineProperties(lineIndex, existingProps);
    }
}, 600);

  function propagateMasterControlNumber(value) {
    debouncedPropagateControlNumber(value);
  }

  function handleVinInput(event) {
    const input = event.target;
    const cleaned = sanitizeVinInput(input.value);
    if (input.value !== cleaned) input.value = cleaned;

    const isValid = validateVin(cleaned);
    const lineEl = input.closest('.cart-line-validation');
    const lineIndex = parseInt(lineEl.dataset.lineIndex, 10);
    const vehicleGroup = lineEl.dataset.vehicleGroup;

    input.classList.toggle('is-valid', isValid && cleaned.length > 0);
    input.classList.toggle('is-invalid', !isValid && cleaned.length > 0);

    const errorEl = lineEl.querySelector('.cart-field__error');
    if (errorEl) errorEl.style.display = (!isValid && cleaned.length > 0) ? 'block' : 'none';

    // Sync to child lines (Temp Tag, Custom Tag) in same vehicle group
    if (vehicleGroup) {
      syncVinToGroup(vehicleGroup, cleaned, lineIndex);
    }

    // Persist to this line's properties
    debouncedSaveLine(lineEl);

    runOverallValidation();
  }

  function syncVinToGroup(groupId, vinValue, originLineIndex) {
    const childInputs = $$(`.cart-line-validation[data-vehicle-group="${groupId}"] input[data-field="vin-synced"]`);
    childInputs.forEach((input) => {
      const childLineEl = input.closest('.cart-line-validation');
      const childLineIndex = parseInt(childLineEl.dataset.lineIndex, 10);
      if (childLineIndex === originLineIndex) return; // don't sync to self
      input.value = vinValue;
      debouncedSaveLine(childLineEl);
    });
  }

  function handlePlateInput(event) {
    const input = event.target;
    const maxChars = parseInt(input.dataset.maxChars, 10) || 7;
    const cleaned = sanitizePlateInput(input.value, maxChars);
    if (input.value !== cleaned) input.value = cleaned;

    const isValid = validatePlate(cleaned, maxChars);

    input.classList.toggle('is-valid', isValid && cleaned.length > 0);
    input.classList.toggle('is-invalid', !isValid && cleaned.length > 0);

    const fieldEl = input.closest('.cart-field');
    const errorEl = fieldEl ? fieldEl.querySelector('.cart-field__error') : null;
    if (errorEl) errorEl.style.display = (!isValid && cleaned.length > 0) ? 'block' : 'none';

    const lineEl = input.closest('.cart-line-validation');
    debouncedSaveLine(lineEl);

    runOverallValidation();
  }

  // ---- Persistence ----

  function collectLineProperties(lineEl) {
    // Delegate to Phase 2's comprehensive collector if loaded
    if (window.MTCartCollectAllProperties) {
      return window.MTCartCollectAllProperties(lineEl);
    }
    
    // Fallback: minimal Phase 1 collector
    const props = {};
    const lineType = lineEl.dataset.lineType;
    const vehicleGroup = lineEl.dataset.vehicleGroup;

    if (vehicleGroup) props['_Vehicle Group'] = vehicleGroup;

    const vinInput = lineEl.querySelector('input[data-field="vin"], input[data-field="vin-synced"]');
    if (vinInput && vinInput.value) props['VIN'] = vinInput.value;

    if (lineType === 'additional' && state.masterControlNumber) {
      if (validateControlNumber(state.masterControlNumber)) {
        props['Control Number'] = state.masterControlNumber;
      }
    }

    const plate1 = lineEl.querySelector('input[data-field="plate-1"]');
    const plate2 = lineEl.querySelector('input[data-field="plate-2"]');
    const plate3 = lineEl.querySelector('input[data-field="plate-3"]');
    if (plate1 && plate1.value) props['Custom Plate 1'] = plate1.value;
    if (plate2 && plate2.value) props['Custom Plate 2'] = plate2.value;
    if (plate3 && plate3.value) props['Custom Plate 3'] = plate3.value;

    return props;
}

  const lineSaveQueue = new Map(); // lineIndex → debounced fn
  function debouncedSaveLine(lineEl) {
    const lineIndex = parseInt(lineEl.dataset.lineIndex, 10);
    if (!lineSaveQueue.has(lineIndex)) {
      lineSaveQueue.set(lineIndex, debounce(async (idx, el) => {
        const props = collectLineProperties(el);
        await updateLineProperties(idx, props);
      }, 600));
    }
    lineSaveQueue.get(lineIndex)(lineIndex, lineEl);
  }

  // ---- Overall validation + checkout gating ----

  function runOverallValidation() {
    const issues = [];

    // 1. Master Control Number (if shown)
    const masterControlEl = $('#cart-master-control-input');
    if (masterControlEl) {
      if (!validateControlNumber(masterControlEl.value)) {
        issues.push('Control Number');
      }
    }

    // 2. Per-line VINs on Additional and Package lines
    const writableVinInputs = $$('.cart-line-validation input[data-field="vin"]');
    writableVinInputs.forEach((input, idx) => {
      if (!validateVin(input.value)) {
        const lineEl = input.closest('.cart-line-validation');
        const handle = lineEl.dataset.productHandle;
        issues.push(`VIN for ${handle}`);
      }
    });

    // 3. Plate texts on Custom Tag lines
    const plateInputs = $$('.cart-line-validation input[data-field^="plate-"]');
    plateInputs.forEach((input) => {
      const maxChars = parseInt(input.dataset.maxChars, 10) || 7;
      if (!validatePlate(input.value, maxChars)) {
        issues.push(`Plate text`);
      }
    });

    const allValid = issues.length === 0;
    updateStatusDisplay(issues.length);
    toggleCheckoutButton(allValid);
  }

  function updateStatusDisplay(issueCount) {
    const statusEl = $('#cart-validation-count');
    const containerEl = $('#cart-validation-status');
    if (!statusEl) return;

    if (issueCount === 0) {
      statusEl.textContent = '✓ All required fields complete. You can proceed to checkout.';
      containerEl.classList.add('is-valid');
    } else {
      statusEl.textContent = `${issueCount} field${issueCount === 1 ? '' : 's'} still need attention.`;
      containerEl.classList.remove('is-valid');
    }
  }

  function toggleCheckoutButton(enabled) {
    const checkoutButtons = $$('button[name="checkout"], input[name="checkout"], #checkout, .cart__checkout-button, [name="checkout"]');
    checkoutButtons.forEach((btn) => {
      if (enabled) {
        btn.disabled = false;
        btn.classList.remove('cart-checkout-button--gated');
        btn.removeAttribute('aria-disabled');
      } else {
        btn.disabled = true;
        btn.classList.add('cart-checkout-button--gated');
        btn.setAttribute('aria-disabled', 'true');
      }
    });
  }

  // ---- Initialization ----

  function init() {
    if (state.initialized) return;

    // Bind master Control Number input
    const masterControlEl = $('#cart-master-control-input');
    if (masterControlEl) {
      // Seed state from pre-filled value (e.g., from cart properties)
      state.masterControlNumber = masterControlEl.value || '';
      masterControlEl.addEventListener('input', handleMasterControlNumberInput);
      // Trigger initial validation
      masterControlEl.dispatchEvent(new Event('input'));
    }

    // Bind VIN inputs (writable on Package/Additional)
    $$('.cart-line-validation input[data-field="vin"]').forEach((input) => {
      input.addEventListener('input', handleVinInput);
    });

    // Bind plate inputs
    $$('.cart-line-validation input[data-field^="plate-"]').forEach((input) => {
      input.addEventListener('input', handlePlateInput);
    });

    // Run initial validation pass (for pre-filled values from concierge URL)
    runOverallValidation();

    state.initialized = true;
    console.log('[cart-validation] initialized');
  }

  // ---- Boot ----
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run init if cart re-renders (Shopify cart drawer / AJAX cart updates)
  document.addEventListener('cart:refresh', init);
  document.addEventListener('cart:updated', init);

  // Expose for debugging
  window.MTCartValidation = { state, runOverallValidation, init };

  // ---- Hide quantity controls on managed-product lines ----
  // Belt-and-suspenders for browsers without :has() support.
  function markValidatedCartItems() {
    document.querySelectorAll('.cart-item').forEach((item) => {
      const hasValidation = item.querySelector('.cart-line-validation');
      if (hasValidation) {
        item.classList.add('has-validation');
      } else {
        item.classList.remove('has-validation');
      }
    });
  }

  // Run on init and on any cart re-render
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', markValidatedCartItems);
  } else {
    markValidatedCartItems();
  }
  document.addEventListener('cart:refresh', markValidatedCartItems);
  document.addEventListener('cart:updated', markValidatedCartItems);
})();